import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, ChevronLeft, Sparkles, CheckCircle2 } from "lucide-react";

const PRESETS = [3, 5, 10, 25];
const IS_DEV = process.env.NODE_ENV !== "production";

export default function Support() {
  const { user } = useAuth();
  const [amount, setAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState("");
  const [stats, setStats] = useState({ total: 0, count: 0, top_supporters: [] });
  const [myTotal, setMyTotal] = useState(0);
  const [thanks, setThanks] = useState(null); // { amount, capture_id } after success
  // Fetched at runtime from the backend so the frontend does NOT bake in a
  // sandbox/live client ID at build time. Single source of truth = backend .env.
  const [paypalConfig, setPaypalConfig] = useState(null); // { client_id, currency, mode }
  const [configError, setConfigError] = useState(null);

  const activeAmount = () => {
    const c = parseFloat(customAmount);
    if (!isNaN(c) && c > 0) return Math.min(c, 10000);
    return amount;
  };

  const refreshData = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/donations/stats"),
        user ? api.get("/donations/me") : Promise.resolve({ data: { total: 0 } }),
      ]);
      setStats(s.data);
      setMyTotal(m.data.total || 0);
    } catch (err) {
      if (IS_DEV) console.error("Failed to load donation stats:", err);
    }
  }, [user]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Fetch PayPal client-id + mode from the backend at runtime (see comment
  // on paypalConfig state above).
  useEffect(() => {
    let cancelled = false;
    api
      .get("/donations/config")
      .then(({ data }) => {
        if (!cancelled) setPaypalConfig(data);
      })
      .catch((err) => {
        if (!cancelled) setConfigError(err);
        if (IS_DEV) console.error("Failed to fetch PayPal config:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const paypalClientId = paypalConfig?.client_id;
  const paypalMode = paypalConfig?.mode;
  const isLiveMode = paypalMode === "live";

  const disabled = !user;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />

      <div className="border-b border-white/5 bg-black/40">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-red-400 flex items-center gap-2">
              <Heart className="w-3 h-3" /> Support the Dojo
            </div>
            <h1 className="font-display text-2xl font-bold mt-1">Tip the Sensei</h1>
          </div>
          <Link
            to={user ? "/dashboard" : "/"}
            className="text-sm text-slate-400 hover:text-white flex items-center gap-1"
            data-testid="support-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        {/* Left — form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="reveal p-8 rounded-lg border border-red-500/30 bg-red-500/5">
            <div className="font-mono text-xs uppercase tracking-widest text-red-400 mb-3">
              One-time gift
            </div>
            <h2 className="font-display text-3xl font-bold leading-tight">
              Keep the EKGs flowing.
            </h2>
            <p className="text-slate-300 mt-3 leading-relaxed">
              EKG Ninja is a passion project. Every tip pays for a rhythm strip,
              a coffee for the sensei, and more belts to earn. Thank you.
            </p>

            {/* Preset amounts */}
            <div className="mt-8">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">
                Pick an amount
              </div>
              <div className="grid grid-cols-4 gap-3">
                {PRESETS.map((v) => {
                  const selected = amount === v && !customAmount;
                  return (
                    <button
                      key={v}
                      onClick={() => {
                        setAmount(v);
                        setCustomAmount("");
                      }}
                      className={`p-4 rounded-md border transition-colors font-display text-xl font-bold ${
                        selected
                          ? "border-green-500/60 bg-green-500/10 text-green-300"
                          : "border-white/15 bg-white/[0.02] hover:border-white/30 text-white"
                      }`}
                      data-testid={`preset-${v}`}
                    >
                      ${v}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4">
                <Input
                  type="number"
                  min="1"
                  max="10000"
                  step="1"
                  placeholder="Or a custom amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="bg-white/5 border-white/10 focus:border-green-500/60 h-11"
                  data-testid="custom-amount-input"
                />
                <div className="mt-2 text-xs text-slate-500 font-mono">
                  Sending{" "}
                  <span className="text-green-400" data-testid="active-amount">
                    ${activeAmount().toFixed(2)}
                  </span>{" "}
                  USD
                </div>
              </div>
            </div>

            {/* PayPal buttons */}
            <div className="mt-8">
              {disabled ? (
                <div className="rounded-md border border-white/10 bg-white/[0.02] p-6 text-center">
                  <p className="text-sm text-slate-300 mb-4">
                    You need a ninja account to leave a tip.
                  </p>
                  <Link to="/register">
                    <Button
                      className="rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold"
                      data-testid="support-signup-btn"
                    >
                      Create free account
                    </Button>
                  </Link>
                </div>
              ) : paypalClientId ? (
                <div data-testid="paypal-buttons-container">
                  {!isLiveMode && (
                    <div
                      className="mb-3 px-3 py-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 text-xs font-mono flex items-center justify-between"
                      data-testid="paypal-sandbox-banner"
                    >
                      <span>SANDBOX MODE — no real money will be charged.</span>
                      <span className="opacity-70">{paypalMode}</span>
                    </div>
                  )}
                  <PayPalScriptProvider
                    // key forces a fresh SDK load when the client-id/mode changes
                    key={`${paypalClientId}-${paypalMode}`}
                    options={{
                      clientId: paypalClientId,
                      currency: paypalConfig?.currency || "USD",
                      intent: "capture",
                    }}
                  >
                    <PayPalButtons
                      key={activeAmount()}
                      style={{
                        layout: "vertical",
                        color: "gold",
                        shape: "pill",
                        label: "donate",
                      }}
                      createOrder={async () => {
                        try {
                          const { data } = await api.post("/donations/create-order", {
                            amount: activeAmount(),
                            currency: "USD",
                          });
                          return data.order_id;
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail) || "Could not start donation");
                          throw e;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          const { data: cap } = await api.post("/donations/capture", {
                            order_id: data.orderID,
                          });
                          if (cap.status === "COMPLETED") {
                            setThanks({ amount: cap.amount, capture_id: cap.capture_id });
                            toast.success(`Thank you! $${cap.amount} received.`);
                            refreshData();
                          } else {
                            toast.error(`Payment status: ${cap.status}`);
                          }
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail) || "Capture failed");
                        }
                      }}
                      onError={(err) => {
                        if (IS_DEV) console.error("PayPal error", err);
                        toast.error("PayPal error — please try again");
                      }}
                      onCancel={() => {
                        toast("Payment cancelled", { icon: "🙅" });
                      }}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : configError ? (
                <div className="text-sm text-red-400 font-mono" data-testid="paypal-config-error">
                  Could not load PayPal configuration — {formatApiError(configError.response?.data?.detail) || configError.message}
                </div>
              ) : paypalConfig === null ? (
                <div className="text-sm text-slate-500 font-mono" data-testid="paypal-config-loading">
                  Loading PayPal…
                </div>
              ) : (
                <div className="text-sm text-red-400 font-mono" data-testid="paypal-config-missing">
                  PayPal is not configured on the server (empty client_id).
                </div>
              )}
            </div>

            {thanks && (
              <div
                className="mt-6 p-4 rounded-md border border-green-500/40 bg-green-500/10 flex items-start gap-3 reveal"
                data-testid="donation-thanks"
              >
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-none mt-0.5" />
                <div>
                  <div className="font-display font-semibold text-green-300">
                    Thank you, ninja — ${thanks.amount} received
                  </div>
                  <div className="text-xs text-slate-400 mt-1 font-mono">
                    Ref: {thanks.capture_id}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right — impact + supporters */}
        <div className="space-y-4">
          <div className="reveal p-5 rounded-lg border border-green-500/30 bg-green-500/5">
            <div className="text-xs font-mono uppercase tracking-widest text-green-400 mb-2">
              Community Total
            </div>
            <div className="font-display text-3xl font-bold" data-testid="stats-total">
              ${stats.total.toFixed(2)}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              from {stats.count} {stats.count === 1 ? "tip" : "tips"}
            </div>
          </div>

          {user && (
            <div className="reveal p-5 rounded-lg border border-white/10 bg-white/[0.03]">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
                Your Support
              </div>
              <div className="font-display text-3xl font-bold" data-testid="my-total">
                ${myTotal.toFixed(2)}
              </div>
              <div className="text-xs text-slate-500 mt-1">Thanks for keeping the dojo alive.</div>
            </div>
          )}

          {stats.top_supporters.length > 0 && (
            <div className="reveal p-5 rounded-lg border border-white/10 bg-white/[0.03]">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-yellow-400" /> Top Supporters
              </div>
              <div className="space-y-2" data-testid="top-supporters">
                {stats.top_supporters.map((s, i) => (
                  <div
                    key={`${s.name}-${i}`}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-slate-200">{s.name}</span>
                    <span className="font-mono text-green-400 font-semibold">
                      ${s.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
